import React, { useState } from 'react';
import UserApproval from './UserApproval';
import AdminReports from './AdminReports';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('users');

  return (
    <div className="main-content">
      <div className="page-header">
        <h1 className="page-title">Admin Panel</h1>
        <p className="page-subtitle">Manage users, tasks, and view reports</p>
      </div>

      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('users')}
          style={{
            ...styles.tab,
            ...(activeTab === 'users' ? styles.activeTab : {}),
          }}
        >
          User Management
        </button>
        <button
          onClick={() => setActiveTab('tasks')}
          style={{
            ...styles.tab,
            ...(activeTab === 'tasks' ? styles.activeTab : {}),
          }}
        >
          Task Management
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          style={{
            ...styles.tab,
            ...(activeTab === 'reports' ? styles.activeTab : {}),
          }}
        >
          Reports
        </button>
      </div>

      <div style={styles.content}>
        {activeTab === 'users' && <UserApproval />}
        {activeTab === 'tasks' && <TaskManagement />}
        {activeTab === 'reports' && <AdminReports />}
      </div>
    </div>
  );
};

const TaskManagement = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ user: '', status: '', active: '' });

  React.useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      const { adminAPI } = await import('../services/api');
      const response = await adminAPI.getAllTasks();
      setTasks(response.data || []);
    } catch (err) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to permanently delete this task?')) {
      try {
        const { adminAPI } = await import('../services/api');
        await adminAPI.deleteTask(id);
        loadTasks();
      } catch (err) {
        alert('Failed to delete task');
      }
    }
  };

  const handleDeactivate = async (id) => {
    try {
      const { adminAPI } = await import('../services/api');
      await adminAPI.deactivateTask(id);
      loadTasks();
    } catch (err) {
      alert('Failed to deactivate task');
    }
  };

  const handleActivate = async (id) => {
    try {
      const { adminAPI } = await import('../services/api');
      await adminAPI.activateTask(id);
      loadTasks();
    } catch (err) {
      alert('Failed to activate task');
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading tasks...</div>;
  }

  const filteredTasks = tasks.filter(task => {
    if (filter.status && task.status !== filter.status) return false;
    if (filter.active !== '' && task.is_active !== parseInt(filter.active)) return false;
    return true;
  });

  return (
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Task Management</h2>
        </div>

        <div style={styles.filters}>
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="form-select"
            style={styles.filterSelect}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
          <select
            value={filter.active}
            onChange={(e) => setFilter({ ...filter, active: e.target.value })}
            className="form-select"
            style={styles.filterSelect}
          >
            <option value="">All Tasks</option>
            <option value="1">Active</option>
            <option value="0">Deactivated</option>
          </select>
        </div>

        {filteredTasks.length === 0 ? (
          <div style={styles.empty}>No tasks found</div>
        ) : (
          <div style={styles.taskList}>
            {filteredTasks.map((task) => (
              <div key={task.id} className="card">
                <div style={styles.taskInfo}>
                  <h3 style={{ marginTop: 0 }}>{task.title}</h3>
                  <p><strong>User ID:</strong> {task.user_id}</p>
                  <p><strong>Status:</strong> <span className={`badge ${task.status === 'completed' ? 'badge-success' : task.status === 'in_progress' ? 'badge-warning' : 'badge-info'}`}>{task.status}</span></p>
                  <p><strong>Priority:</strong> <span className={`badge ${task.priority === 'high' ? 'badge-danger' : task.priority === 'medium' ? 'badge-warning' : 'badge-success'}`}>{task.priority}</span></p>
                  <p><strong>Created:</strong> {new Date(task.created_at).toLocaleString()}</p>
                  {task.is_active === 0 && (
                    <p className="badge badge-danger">Deactivated</p>
                  )}
                </div>
                <div style={styles.actions}>
                  {task.is_active === 1 ? (
                    <button
                      onClick={() => handleDeactivate(task.id)}
                      className="btn btn-secondary"
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => handleActivate(task.id)}
                      className="btn btn-success"
                    >
                      Activate
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(task.id)}
                    className="btn btn-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
  );
};

const styles = {
  tabs: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    borderBottom: '2px solid #e5e7eb',
  },
  tab: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontSize: '0.9375rem',
    color: '#64748b',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  activeTab: {
    color: '#2563eb',
    borderBottom: '2px solid #2563eb',
  },
  content: {
    marginTop: '2rem',
  },
  section: {
    padding: '1.5rem 0',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  filterSelect: {
    maxWidth: '200px',
  },
  taskList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  taskCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  taskInfo: {
    flex: 1,
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  empty: {
    textAlign: 'center',
    padding: '3rem',
    color: '#64748b',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  loading: {
    textAlign: 'center',
    padding: '4rem 2rem',
    color: '#64748b',
  },
};

export default AdminPanel;
